import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import Pagination from "../../Components/pagination/Pagination";
import { apiDelete, apiGet } from "../../Services/Services";
import { ENDPOINTS } from "../../Services/Endpoints";

export default function AdminRoles() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(ENDPOINTS.marketing.admins, {
        params: { page, limit: perPage },
      });

      const list = data?.data?.data ?? [];
      const total = data?.data?.total ?? 0;
      const pages = data?.data?.totalPages ?? Math.max(1, Math.ceil(total / perPage));

      setAdmins(list);
      setTotalCount(total);
      setTotalPages(pages);

      if (page > pages) {
        setPage(pages);
      }
    } catch {
      setAdmins([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const closeRevokeModal = () => {
    if (!revokeSubmitting) {
      setRevokeTarget(null);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;

    setRevokeSubmitting(true);
    try {
      await apiDelete(ENDPOINTS.marketing.adminById(revokeTarget.id), {}, {
        showSuccessToast: true,
        useApiSuccessMessage: true,
      });

      setRevokeTarget(null);

      if (admins.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        await fetchAdmins();
      }
    } catch {
      // Errors handled by api interceptor
    } finally {
      setRevokeSubmitting(false);
    }
  };

  const safePage = Math.min(page, totalPages);
  const start = totalCount === 0 ? 0 : (safePage - 1) * perPage + 1;
  const end = Math.min(safePage * perPage, totalCount);

  const revokeName = revokeTarget?.firstName || revokeTarget?.email || "this admin";
  const revokeRole =
    typeof revokeTarget?.role === "string"
      ? revokeTarget.role
      : revokeTarget?.role?.name;

  return (
    <>
      <div className="card cms-table-card">
        <div className="cms-table-wrap">
          <table className="cms-data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th className="cms-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", color: "#6b7280" }}>
                    Loading admins...
                  </td>
                </tr>
              ) : admins.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", color: "#6b7280" }}>
                    No admins found.
                  </td>
                </tr>
              ) : (
                admins.map((admin) => (
                  <tr key={admin.id}>
                    <td>{admin.firstName}</td>
                    <td>{admin.email}</td>
                    <td>
                      <span className="cms-role-display">
                        {typeof admin.role === "string" ? admin.role : admin.role?.name}
                      </span>
                    </td>
                    <td className="cms-col-actions">
                      <button
                        type="button"
                        className="cms-revoke-btn"
                        onClick={() => setRevokeTarget(admin)}
                      >
                        Revoke Access
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <Pagination
            variant="full"
            summary={`Showing ${admins.length ? start : 0}-${end} of ${totalCount}`}
            page={safePage}
            totalPages={totalPages}
            totalCount={totalCount}
            perPage={perPage}
            onPageChange={(nextPage) =>
              setPage(Math.max(1, Math.min(nextPage, totalPages)))
            }
            onPerPageChange={(val) => {
              setPerPage(val);
              setPage(1);
            }}
          />
        </div>
      </div>

      {revokeTarget && (
        <div className="modal-overlay" onClick={closeRevokeModal}>
          <div
            className="modal-card cms-revoke-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cms-revoke-title"
          >
            <div className="modal-header">
              <div>
                <h2 id="cms-revoke-title">Revoke Access</h2>
                <p>Are you sure you want to revoke access?</p>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeRevokeModal}
                disabled={revokeSubmitting}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="cms-revoke-modal-body">
              <p>
                <strong>{revokeName}</strong>
                {revokeTarget.email ? (
                  <>
                    <br />
                    <span className="cms-revoke-email">{revokeTarget.email}</span>
                  </>
                ) : null}
                {revokeRole ? (
                  <>
                    <br />
                    <span className="cms-revoke-role">Role: {revokeRole}</span>
                  </>
                ) : null}
              </p>
            </div>

            <div className="modal-footer-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={closeRevokeModal}
                disabled={revokeSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-confirm-refund"
                onClick={confirmRevoke}
                disabled={revokeSubmitting}
              >
                {revokeSubmitting ? "Revoking..." : "Confirm Revoke"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
